/**
 * MCPTTClient — typed WebSocket client for the MCPTT manager.
 *
 * Reverse-engineered from `bundle-source/FleetConsole/api/ServerApi.js`.
 * Wire spec: docs/protocol.md.
 *
 * UI-free, dependency-free, browser-only. Intended to be embedded in the
 * dispatcher rebuild or any other MCPTT tooling (mobile reskin, monitors,
 * test harnesses).
 *
 * Audio capture/playback is delegated to an `AudioAdapter`. Phase 1
 * ships a no-op stub so PTT messages can fire without throwing even when
 * no audio is wired. Phase 2 will provide a real Web Audio adapter.
 */

import type {
  AudioAdapter,
  Base64Guid,
  Call,
  ConnectionState,
  DataExMessage,
  Device,
  DeviceContextMessage,
  DeviceParametersPushMessage,
  EmergencyAckMessage,
  EmergencyMessage,
  IncomingMessage,
  LoginResponseMessage,
  MCPTTClientEvents,
  Network,
  PttControlMessage,
  ServerConfigMessage,
  VoicePacketMessage,
} from "./types";

import {
  DataOp,
  LoginResponseCode,
  ObjectType,
  PttControlType,
  PttRequestType,
  TargetType,
} from "./types";

import {
  base64ToUint8Array,
  getBase64ID,
  getDeviceID,
  uint8ArrayToBase64,
} from "./utils";

// ---------------------------------------------------------------
// Constants
// ---------------------------------------------------------------

const DEFAULT_APP_NAME      = "FleetConsole";
const DEFAULT_APP_VERSION   = "1.0.0";
const DEFAULT_RECONNECT_MS  = 5000;
/** Default ping cadence used until DEVICE_CONTEXT is received. The manager
 *  tells us its tolerance via PingTimeout (typically 10000ms); we send at
 *  PingTimeout/2. */
const DEFAULT_PING_INTERVAL_MS = 5_000;
const DEFAULT_DEAD_AFTER_MS    = 12_000;

const NULL_GUID_B64 = "AAAAAAAAAAAAAAAAAAAAAA==";

// LOGIN_RESPONSE.Response → human-readable message, mirrored from the spec
// in docs/protocol.md section 3.5
const LOGIN_RESPONSE_MESSAGES: Record<number, string> = {
  [LoginResponseCode.Success]:                "Successful login",
  [LoginResponseCode.InvalidVersion]:         "Invalid client version",
  [LoginResponseCode.InvalidCredentials]:     "Invalid user name or password",
  [LoginResponseCode.LicenseExpired]:         "License is expired",
  [LoginResponseCode.TooManyConnections]:     "Exceeded number of available user connections",
  [LoginResponseCode.DemoModeMustRestart]:    "Server working in demo mode must be restarted",
  [LoginResponseCode.MultipleLoginsProhibited]:
    "Multiple logins are prohibited for this user; another device is already connected",
};

// ---------------------------------------------------------------
// Options
// ---------------------------------------------------------------

export interface MCPTTClientOptions {
  /** "host:port" — bare authority, no scheme. WS scheme picked from page protocol. */
  serverAddress: string;

  /** App identifier shown in server logs. Defaults to "FleetConsole" so the
   *  manager treats us as the standard dispatcher and we don't need a new
   *  client license. */
  appName?: string;
  /** Reported app version. Cosmetic, but the manager logs it. */
  appVersion?: string;

  /** Auto-reconnect on transport-level close (default true). */
  autoReconnect?: boolean;
  /** Delay between reconnect attempts (default 5000ms). */
  reconnectDelayMs?: number;

  /** Audio adapter — null for text/data dispatch only. */
  audio?: AudioAdapter | null;

  /** Verbose console logging of every frame (default false). */
  debug?: boolean;
}

// ---------------------------------------------------------------
// Re-export AudioAdapter so consumers don't have to import from types
// ---------------------------------------------------------------

export type { AudioAdapter } from "./types";

// ---------------------------------------------------------------
// Default no-op AudioAdapter (used when options.audio is null/undefined)
// ---------------------------------------------------------------

const NULL_AUDIO: AudioAdapter = {
  start() { return Promise.resolve(); },
  stop() {},
  playFrame() {},
};

// ---------------------------------------------------------------
// Client
// ---------------------------------------------------------------

export class MCPTTClient {
  // --- options ---
  private readonly serverAddress: string;
  private readonly appName: string;
  private readonly appVersion: string;
  private readonly autoReconnect: boolean;
  private readonly reconnectDelayMs: number;
  private readonly audio: AudioAdapter;
  private readonly debug: boolean;

  // --- transport ---
  private ws: WebSocket | null = null;
  private closing = false;          // true = manual disconnect, skip reconnect
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // --- handshake bookkeeping ---
  private pendingLogin: { user: string; password: string } | null = null;
  private loginResolve: ((v: { userId: Base64Guid }) => void) | null = null;
  private loginReject:  ((err: Error) => void) | null = null;
  private connectResolve: (() => void) | null = null;
  private connectReject:  ((err: Error) => void) | null = null;

  // --- session/audio config received from manager ---
  // Exposed publicly via the `audioConfig` getter so the AudioAdapter
  // implementation in phase 2 can read these without needing to listen
  // for raw SERVER_CONFIG.
  private _voipPort = 0;
  private _audioSampleRate = 8000;
  private _audioBitRate    = 64000;
  private _audioFrameSize  = 20;
  private _audioCodec      = 1;
  private _ssrc            = 0;
  private _bufferSamplesCount = 0;

  // --- ping/pong ---
  // Cadence is tuned dynamically from DEVICE_CONTEXT.PingTimeout (we send
  // at PingTimeout/2 and treat no traffic for PingTimeout+jitter as dead).
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastSeenAt = 0;
  private deadCheckTimer: ReturnType<typeof setInterval> | null = null;
  private pingIntervalMs = DEFAULT_PING_INTERVAL_MS;
  private deadAfterMs    = DEFAULT_DEAD_AFTER_MS;
  /** Set by the manager via DEVICE_CONTEXT.SecondaryNetworkAddress. Used
   *  by reconnect logic if the primary keeps failing. Exposed via the
   *  `secondaryAddress` getter for diagnostics. */
  private _secondaryAddress: string | null = null;

  // --- state ---
  private _state: ConnectionState = "disconnected";
  private _userId: Base64Guid | null = null;
  private _networks: Network[] = [];
  private _calls: Call[] = [];

  // --- networks where we currently hold the floor (for VOICE_PACKET out) ---
  private pttNetworkIDs: Base64Guid[] = [];

  // CallIDs we have already PTT_RESPONSE-ACK'd. The manager's
  // protocol requires every PTT_CONTROL "begin" batch (Begin/Enter/
  // Pressed) to be ACKd by a PTT_RESPONSE; without it the dispatcher
  // is treated as a passive observer and won't receive any
  // VOICE_PACKETs for the group going forward. Tracked per CallID so
  // we don't double-ack the Begin/Enter/Pressed triplet — the legacy
  // FleetConsole sends exactly one ack per call.
  private ackedCallIds = new Set<Base64Guid>();

  // Groups we've "tuned to" this session. The manager requires the
  // dispatcher to have at least one PTT_REQUEST GroupVoiceStart/Stop
  // pair on a group before it will fan voice frames TO that
  // dispatcher for that group. tuneToGroup() does this dance once
  // per group; this Set prevents redundant phantom calls.
  private tunedGroupIds = new Set<Base64Guid>();

  // Talkgroups / private targets muted locally in this dispatcher.
  // Muting is local only: we still ACK PTT_CONTROL and keep state
  // updated, but drop VOICE_PACKET playback for matching call.TargetID.
  private mutedTargetIds = new Set<Base64Guid>();

  // --- diagnostic counters ---
  // Tracked so the DiagnosticsPanel can show "is voice actually
  // arriving" without the operator opening DevTools. Updated on every
  // VOICE_PACKET in/out plus PTT_CONTROL pressed/released so we can
  // detect the "PTT happens but zero voice" case (caused by missing
  // AllowedGroups / ScanList on the dispatcher account).
  private _stats = {
    voiceRxCount:    0,
    voiceRxBytes:    0,
    voiceRxLastTs:   0 as number,
    voiceTxCount:    0,
    voiceTxBytes:    0,
    voiceTxLastTs:   0 as number,
    /** Wall-clock ms when we last saw a *_PRESSED PTT_CONTROL frame. */
    lastPressedTs:   0 as number,
    /** Wall-clock ms when we last saw a *_RELEASED PTT_CONTROL frame. */
    lastReleasedTs:  0 as number,
    /** Number of times a press completed (released or ended) with
     *  zero VOICE_PACKETs received during the press window. Strong
     *  signal that the dispatcher account isn't authorized for the
     *  group on the manager. */
    silentCalls:     0,
  };

  // --- typed event listeners ---
  // We don't extend EventTarget — we maintain our own listener map so the
  // public on/off API can pass typed args directly (CustomEvent.detail
  // would force consumers to unpack a single arg).
  //
  // Stored loosely as Set<Function>; the public on/off/emit signatures
  // re-impose the per-event types from MCPTTClientEvents.
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  constructor(opts: MCPTTClientOptions) {
    if (!opts || !opts.serverAddress) {
      throw new Error("MCPTTClient: serverAddress is required");
    }
    this.serverAddress    = opts.serverAddress;
    this.appName          = opts.appName    ?? DEFAULT_APP_NAME;
    this.appVersion       = opts.appVersion ?? DEFAULT_APP_VERSION;
    this.autoReconnect    = opts.autoReconnect ?? true;
    this.reconnectDelayMs = opts.reconnectDelayMs ?? DEFAULT_RECONNECT_MS;
    this.audio            = opts.audio ?? NULL_AUDIO;
    this.debug            = !!opts.debug;
  }

  // ============================================================
  // Public read-only state
  // ============================================================

  get state():    ConnectionState                  { return this._state; }
  get userId():   Base64Guid | null                { return this._userId; }
  get networks(): ReadonlyArray<Network>           { return this._networks; }
  get calls():    ReadonlyArray<Call>              { return this._calls; }

  /** Live diagnostic snapshot. Returned by reference for cheapness
   *  but treat as read-only. Tail-call by polling at ~1 Hz. */
  getStats(): {
    voiceRxCount:    number;
    voiceRxBytes:    number;
    voiceRxLastTs:   number;
    voiceTxCount:    number;
    voiceTxBytes:    number;
    voiceTxLastTs:   number;
    lastPressedTs:   number;
    lastReleasedTs:  number;
    silentCalls:     number;
    tunedGroups:     number;
    mutedTargets:    number;
  } {
    return {
      ...this._stats,
      tunedGroups:  this.tunedGroupIds.size,
      mutedTargets: this.mutedTargetIds.size,
    };
  }

  setTargetMuted(targetId: Base64Guid, muted: boolean): void {
    if (muted) this.mutedTargetIds.add(targetId);
    else this.mutedTargetIds.delete(targetId);
    // Apply to current calls immediately. Future calls are checked in
    // handleVoicePacket().
    for (const c of this._calls) {
      if (c.TargetID === targetId) c.SoundIsMuted = muted;
    }
  }

  isTargetMuted(targetId: Base64Guid): boolean {
    return this.mutedTargetIds.has(targetId);
  }

  /** Audio negotiation result from SERVER_CONFIG. The phase-2
   *  AudioAdapter reads this to set up its capture/playback pipeline. */
  get audioConfig(): {
    voipPort: number;
    sampleRate: number;
    bitRate: number;
    frameSizeMs: number;
    bufferSamples: number;
    codec: number;
    ssrc: number;
  } {
    return {
      voipPort:      this._voipPort,
      sampleRate:    this._audioSampleRate,
      bitRate:       this._audioBitRate,
      frameSizeMs:   this._audioFrameSize,
      bufferSamples: this._bufferSamplesCount,
      codec:         this._audioCodec,
      ssrc:          this._ssrc,
    };
  }

  /** Manager-supplied failover address (host:port). Reconnect can target
   *  this if the primary keeps failing. Null until DEVICE_CONTEXT arrives. */
  get secondaryAddress(): string | null { return this._secondaryAddress; }

  // ============================================================
  // Lifecycle
  // ============================================================

  /**
   * Open the WebSocket. Resolves as soon as the transport is up
   * (before any handshake messages are exchanged); rejects if the
   * socket fails to open.
   *
   * IMPORTANT: connect() does NOT advance the handshake by itself.
   * The manager begins the handshake by sending SERVER_CONFIG, which
   * we can only respond to with DEVICE_CONFIG (carrying credentials)
   * once login() has stashed them. The expected order is:
   *
   *   await client.connect();
   *   const { userId } = await client.login("warren", "...");
   *
   * It is also safe to call them in either order — login() will buffer
   * the credentials and send DEVICE_CONFIG either immediately (if
   * SERVER_CONFIG already arrived) or as soon as it does.
   */
  connect(): Promise<void> {
    if (this._state !== "disconnected" && this._state !== "error" && this._state !== "reconnecting") {
      // Already connecting / connected — resolve when the socket is open.
      return new Promise<void>((res, rej) => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          res();
        } else {
          this.connectResolve = res;
          this.connectReject  = rej;
        }
      });
    }
    return this.openSocket();
  }

  private openSocket(): Promise<void> {
    return new Promise<void>((res, rej) => {
      this.connectResolve = res;
      this.connectReject  = rej;

      if (typeof WebSocket === "undefined") {
        const e = new Error("WebSocket not available in this environment");
        this.setState("error");
        rej(e);
        return;
      }

      this.closing = false;
      this.setState("connecting");

      const protocol = (typeof location !== "undefined" && location.protocol === "https:") ? "wss:" : "ws:";
      const url = `${protocol}//${this.serverAddress}`;

      try {
        this.ws = new WebSocket(url);
      } catch (err) {
        this.setState("error");
        rej(err as Error);
        return;
      }

      this.ws.onopen    = () => this.handleOpen();
      this.ws.onmessage = (e) => this.handleMessage(e);
      this.ws.onerror   = (e) => this.handleError(e);
      this.ws.onclose   = (e) => this.handleClose(e);
    });
  }

  /**
   * Send credentials and complete the handshake. Resolves with the
   * assigned UserID on success, rejects with an Error whose `.code` is
   * the numeric LoginResponseCode on failure.
   *
   *   const { userId } = await client.login("dispatcher1", "secret");
   *
   * If the socket isn't open yet, login() will open it (calls connect()
   * internally). The handshake then unfolds as:
   *
   *   [WS open] -> SERVER_CONFIG -> DEVICE_CONFIG (with creds) ->
   *   CONFIG_SERVER_RESPONSE_ACK -> LOGIN -> LOGIN_RESPONSE
   *
   * The promise resolves at LOGIN_RESPONSE (success); the next step
   * (BEGIN_INITIALIZE / END_INITIALIZE) drives the `state` event so
   * UI can render a loading screen until "ready".
   */
  login(user: string, password: string): Promise<{ userId: Base64Guid }> {
    if (!user) return Promise.reject(new Error("login: user is required"));

    // Stash credentials so handleServerConfig can pick them up. This MUST
    // happen before we open the socket (or we race SERVER_CONFIG arrival).
    this.pendingLogin = { user, password };

    const promise = new Promise<{ userId: Base64Guid }>((res, rej) => {
      this.loginResolve = res;
      this.loginReject  = rej;
    });

    // Make sure we have a transport. If we're already at "configuring"
    // (SERVER_CONFIG already arrived but we hadn't sent DEVICE_CONFIG
    // yet because no creds), or already authenticated and re-logging in,
    // dispatch DEVICE_CONFIG now.
    if (this._state === "disconnected" || this._state === "error" || this._state === "reconnecting") {
      // Open the socket; SERVER_CONFIG will trigger sendDeviceConfig
      // once it arrives, since pendingLogin is now set.
      this.openSocket().catch((err) => {
        if (this.loginReject) {
          this.loginReject(err as Error);
          this.loginResolve = this.loginReject = null;
        }
      });
    } else if (this._state === "configuring" || this._state === "ready") {
      this.sendDeviceConfig();
    }
    // For other intermediate states (connecting, logging-in, initializing)
    // we just wait — the in-flight handshake will pick up our credentials.

    return promise;
  }

  /**
   * Close the WebSocket and stop reconnection attempts.
   */
  disconnect(): void {
    this.closing = true;
    this.clearReconnect();
    this.clearPingTimers();
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.setState("disconnected");
  }

  // ============================================================
  // Actions (client -> server)
  // ============================================================

  /**
   * "Tune in" to a group's voice fan-out.
   *
   * Empirically (HAR captured on 2026-04-25 against Fleet Server
   * 5.8.2.1), the manager only delivers VOICE_PACKETs to a dispatcher
   * for a group AFTER that dispatcher has issued at least one
   * PTT_REQUEST GroupVoiceStart/Stop pair on the group during the
   * session. Until then, PTT_CONTROL events flow but voice doesn't —
   * even with OP_MONITORING granted and PTT_RESPONSE ACKs sent.
   *
   * tuneToGroup() does this dance silently — it sends just the wire
   * frames (no audio adapter touch, no mic activation, no actual
   * voice frames). The result is a brief phantom call attributed to
   * this dispatcher in the manager's call log, after which radio
   * voice on that group will fan out to us for the rest of the
   * session.
   *
   * Idempotent — re-calling for an already-tuned group is a no-op.
   *
   * Caveats:
   * - Creates one phantom call per group per session in the call log.
   * - Briefly takes the floor — if a radio is mid-press when we tune,
   *   we may bump them. The 250 ms hold is short enough that this is
   *   almost never noticeable in practice.
   */
  async tuneToGroup(opts: { networkId: Base64Guid; groupId: Base64Guid }): Promise<void> {
    if (this.tunedGroupIds.has(opts.groupId)) return;
    this.tunedGroupIds.add(opts.groupId);
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Not connected; let the next call try again
      this.tunedGroupIds.delete(opts.groupId);
      return;
    }
    this.send({
      MessageID:   "PTT_REQUEST",
      NetworkID:   opts.networkId,
      Destination: opts.groupId,
      Type:        PttRequestType.GroupVoiceStart,
    });
    // FleetConsole's first successful receive path happens only after
    // the dispatcher actually emits VOICE_PACKETs on a group. A bare
    // start/stop is not enough. Send two short µ-law silence frames
    // using the same dynamic RTP payload type (106 / 0x6a) and no
    // CallID prefix, matching legacy outbound frames:
    //   80 6A <seq> 00000000 <ssrc> D5 D5 ...
    this.sendSilenceVoicePacket(opts.networkId, 1);
    await new Promise((r) => setTimeout(r, 120));
    this.sendSilenceVoicePacket(opts.networkId, 2);
    // Brief hold so the manager registers the call before we release.
    // Empirically 200ms is plenty; we go a bit higher for safety.
    await new Promise((r) => setTimeout(r, 180));
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.send({
      MessageID:   "PTT_REQUEST",
      NetworkID:   opts.networkId,
      Destination: opts.groupId,
      Type:        PttRequestType.GroupVoiceStop,
    });
  }

  /** Send one FleetConsole-style µ-law silence RTP packet. Used only
   *  for tuneToGroup() so the manager registers this dispatcher as an
   *  audio participant for the selected group. */
  private sendSilenceVoicePacket(networkId: Base64Guid, seq: number): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // Use server frame size if it looks sample-like; otherwise fall
    // back to 60ms at advertised sample rate. Fleet Server 5.8 reports
    // AudioFrameSize=60 and sampleRate=16000; legacy frames are about
    // 960 bytes of payload, exactly 60ms of 16k µ-law.
    const payloadSamples = this._audioFrameSize > 200
      ? this._audioFrameSize
      : Math.max(160, Math.round((this._audioSampleRate || 16000) * (this._audioFrameSize || 60) / 1000));
    const frame = new Uint8Array(12 + payloadSamples);
    frame[0] = 0x80;     // RTP V=2
    frame[1] = 0x6a;     // FleetConsole dynamic voice payload type
    frame[2] = (seq >> 8) & 0xff;
    frame[3] = seq & 0xff;
    // timestamp bytes 4..7 remain zero (FleetConsole does this)
    frame[8]  = (this._ssrc >>> 24) & 0xff;
    frame[9]  = (this._ssrc >>> 16) & 0xff;
    frame[10] = (this._ssrc >>> 8)  & 0xff;
    frame[11] = this._ssrc & 0xff;
    frame.fill(0xd5, 12); // µ-law silence
    const Data = uint8ArrayToBase64(frame);
    this.send({ MessageID: "VOICE_PACKET", NetworkID: networkId, Data });
  }

  startPTT(opts: { networkId: Base64Guid; destinationId: Base64Guid; type?: PttRequestType }): void {
    const type = opts.type ?? PttRequestType.GroupVoiceStart;
    this.pttNetworkIDs = [opts.networkId];
    this.send({
      MessageID: "PTT_REQUEST",
      NetworkID: opts.networkId,
      Destination: opts.destinationId,
      Type: type,
    });
    // Kick off audio capture. The adapter callback wires straight
    // into our outgoing-frame sender.
    void this.audio.start(
      (frame) => this.sendVoicePacket(frame),
      {
        sampleRate: this._audioSampleRate,
        frameSizeMs: this._audioFrameSize,
        codec: this._audioCodec,
        ssrc: this._ssrc,
      },
    );
  }

  stopPTT(opts: { networkId: Base64Guid; destinationId: Base64Guid; type?: PttRequestType }): void {
    const type = opts.type ?? PttRequestType.GroupVoiceStop;
    this.send({
      MessageID: "PTT_REQUEST",
      NetworkID: opts.networkId,
      Destination: opts.destinationId,
      Type: type,
    });
    this.audio.stop();
    this.pttNetworkIDs = [];
  }

  requestGps(opts: {
    networkId: Base64Guid;
    deviceId: Base64Guid;
    groupId?: Base64Guid;
    periodSec?: number;
  }): Base64Guid {
    const RequestID = getBase64ID();
    const devices = opts.deviceId ? { Devices: [opts.deviceId] } : {};
    const groups  = opts.groupId ? { Groups: [opts.groupId] } : {};
    const period = opts.periodSec ?? 0;
    this.send({
      // FleetConsole wire name. Older guessed GPS_REQUEST is ignored
      // by Fleet Server 5.8.3; HAR from original dispatcher confirms
      // GPS_DEVICE_REQUEST returns immediate GPS_RESULT_TO_CLIENT.
      MessageID: "GPS_DEVICE_REQUEST",
      NetworkID: opts.networkId,
      RequestID,
      Timing:    period ? 1 : 0,
      Period:    period,
      ...devices,
      ...groups,
      // Defensive compatibility for older server builds that used
      // singular fields. FleetConsole does not need these, but they
      // should be harmless if ignored.
      DeviceID:  opts.deviceId,
      GroupID:   opts.groupId ?? NULL_GUID_B64,
      RequestId: RequestID,
    });
    return RequestID;
  }

  cancelGps(opts: { networkId: Base64Guid; requestId: Base64Guid }): void {
    const RequestId = getBase64ID();
    this.send({
      MessageID: "GPS_DEVICE_CANCEL",
      NetworkID: opts.networkId,
      RequestId,
      CancelRequestID: opts.requestId,
      RequestID: RequestId,
      CancelRequestId: opts.requestId,
    });
  }

  /**
   * Acknowledge an active emergency. The wire requires NetworkID — pass
   * the same NetworkID from the EMERGENCY frame you received.
   */
  ackEmergency(opts: { emergencyId: Base64Guid; networkId: Base64Guid }): void {
    this.send({
      MessageID:   "EMERGENCY_ACK",
      EmergencyID: opts.emergencyId,
      NetworkID:   opts.networkId,
    });
  }

  /**
   * Cancel an active emergency. NetworkID is required by symmetry with
   * EMERGENCY_ACK.
   */
  cancelEmergency(opts: { emergencyId: Base64Guid; networkId: Base64Guid }): void {
    this.send({
      MessageID:   "EMERGENCY_CANCEL",
      EmergencyID: opts.emergencyId,
      NetworkID:   opts.networkId,
    });
  }

  /**
   * Minimal text-message send. Returns the JobID — track delivery via
   * STORAGE_JOB_STATE messages (currently surfaced through "raw" event).
   *
   * Note: the protocol's full text/file lifecycle is in
   * docs/protocol.md section 8. Phase 2 will add an Outbox helper.
   */
  sendText(opts: {
    networkId: Base64Guid;
    targetId: Base64Guid;
    targetType: TargetType;
    text: string;
  }): Base64Guid {
    const JobID = getBase64ID();
    this.send({
      MessageID: "STORAGE_JOB_REGISTER",
      JobID,
      NetworkID:  opts.networkId,
      TargetID:   opts.targetId,
      TargetType: opts.targetType,
      Type: 0,                 // 0 = text
      Text: opts.text,
    });
    return JobID;
  }

  markRead(opts: { networkId: Base64Guid; jobId: Base64Guid }): void {
    this.send({
      MessageID: "MARK_AS_READ",
      JobID:     opts.jobId,
      NetworkID: opts.networkId,
    });
  }

  // ============================================================
  // Typed event API
  // ============================================================

  on<E extends keyof MCPTTClientEvents>(event: E, cb: MCPTTClientEvents[E]): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb as (...args: unknown[]) => void);
    return () => this.off(event, cb);
  }

  off<E extends keyof MCPTTClientEvents>(event: E, cb: MCPTTClientEvents[E]): void {
    const set = this.listeners.get(event);
    if (set) set.delete(cb as (...args: unknown[]) => void);
  }

  private emit<E extends keyof MCPTTClientEvents>(
    event: E,
    ...args: Parameters<MCPTTClientEvents[E]>
  ): void {
    const set = this.listeners.get(event);
    if (!set) return;
    // Snapshot so a listener calling off() doesn't mutate iteration.
    for (const cb of Array.from(set)) {
      try {
        cb(...(args as unknown[]));
      } catch (err) {
        // A throwing listener must not break the dispatch loop.
        // eslint-disable-next-line no-console
        console.error("MCPTTClient: listener for", event, "threw:", err);
      }
    }
  }

  // ============================================================
  // Transport handlers
  // ============================================================

  private handleOpen(): void {
    this.log("ws open");
    this.lastSeenAt = Date.now();
    this.startPingTimers();

    // Resolve any pending connect() — transport is up. The handshake
    // (SERVER_CONFIG -> DEVICE_CONFIG -> ACK -> LOGIN -> LOGIN_RESPONSE)
    // is driven by login() once it has stashed credentials.
    if (this.connectResolve) {
      this.connectResolve();
      this.connectResolve = this.connectReject = null;
    }
  }

  private handleError(event: Event): void {
    this.log("ws error", event);
    this.emit("transport-error", event);
  }

  private handleClose(event: CloseEvent): void {
    this.log("ws close", event.code, event.reason);
    this.clearPingTimers();
    this.ws = null;

    this.emit("disconnect", event.code, event.reason);

    if (this.connectReject && this._state !== "ready") {
      this.connectReject(new Error(`WebSocket closed (${event.code}): ${event.reason || "no reason"}`));
      this.connectResolve = this.connectReject = null;
    }

    if (this.closing) {
      this.setState("disconnected");
      return;
    }

    // Auto-reconnect on broken transport (1006) or server-initiated close (1005).
    if (this.autoReconnect && (event.code === 1005 || event.code === 1006 || event.code >= 4000)) {
      if (event.code === 1005) {
        // Server halt — drop cached state, mirroring ServerApi.clearState()
        this.clearCachedState();
      }
      this.setState("reconnecting");
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        void this.openSocket().catch(() => {/* will retry on next close */});
      }, this.reconnectDelayMs);
    } else {
      this.setState("disconnected");
    }
  }

  private handleMessage(event: MessageEvent): void {
    this.lastSeenAt = Date.now();

    let messages: IncomingMessage[];
    try {
      messages = JSON.parse(event.data as string);
    } catch (err) {
      this.log("non-JSON frame, ignoring", err);
      return;
    }
    if (!Array.isArray(messages)) {
      // Manager always wraps in array, but be defensive.
      messages = [messages];
    }
    for (const msg of messages) this.dispatchMessage(msg);
  }

  // ============================================================
  // Message dispatch
  // ============================================================

  private dispatchMessage(msg: IncomingMessage): void {
    if (this.debug) this.log("←", msg.MessageID, msg);

    switch (msg.MessageID) {
      case "SERVER_CONFIG":
        this.handleServerConfig(msg as ServerConfigMessage);
        return;

      case "CONFIG_SERVER_RESPONSE_ACK":
        // Manager accepted DEVICE_CONFIG. Send LOGIN to advance.
        this.setState("logging-in");
        this.send({ MessageID: "LOGIN" });
        return;

      case "CONFIG_SERVER_RESPONSE_NACK": {
        const reason = (msg as { Reason?: string }).Reason ?? "Server rejected DEVICE_CONFIG";
        const err = new Error(reason);
        this.setState("error");
        if (this.connectReject) { this.connectReject(err); this.connectResolve = this.connectReject = null; }
        if (this.loginReject)   { this.loginReject(err);   this.loginResolve   = this.loginReject   = null; }
        this.emit("auth-error", LoginResponseCode.InvalidCredentials, reason);
        return;
      }

      case "LOGIN_RESPONSE":
        this.handleLoginResponse(msg as LoginResponseMessage);
        return;

      case "BEGIN_INITIALIZE":
        this.setState("initializing");
        return;

      case "END_INITIALIZE":
        this.setState("ready");
        this.emit("ready");
        return;

      case "DATAEX":
        this.handleDataEx(msg as DataExMessage);
        return;

      case "PTT_CONTROL":
        this.handlePttControl(msg as PttControlMessage);
        return;

      case "VOICE_PACKET":
        this.handleVoicePacket(msg as VoicePacketMessage);
        return;

      case "EMERGENCY":
        this.emit("emergency", msg as EmergencyMessage);
        return;

      case "EMERGENCY_ACK":
        this.emit("emergency:ack", msg as EmergencyAckMessage);
        return;

      case "GPS_RESULT_TO_CLIENT":
        this.handleGpsResult(msg as { NetworkID: Base64Guid; DeviceID: Base64Guid;
          Latitude: number; Longitude: number; Accuracy: number;
          Altitude?: number; Speed?: number; Time: number; });
        return;

      case "STATUS_CHANGE_REQUEST_FOR_DEVICE":
        this.handleStatusChange(msg as unknown as { NetworkID: Base64Guid; DeviceID: Base64Guid; StatusID: Base64Guid });
        return;

      case "DEVICE_CONTEXT":
        this.handleDeviceContext(msg as DeviceContextMessage);
        return;

      case "PING":
        this.send({ MessageID: "PONG" });
        return;

      case "PONG":
        return;

      // Forward everything else as a raw event so consumers can react
      // without us having to model every message exhaustively.
      case "PTT_RESPONSE":
      case "STORAGE_JOB_STATE":
      case "STORAGE_JOB_REQUEST":
      case "STORAGE_JOB_CONTENT":
      case "STORAGE_JOB_CONTENT_REQUEST":
      case "STORAGE_JOB_INFO":
      case "OTAP_SETTINGS_RESPONSE":
      case "GPS_RESPONSE":
      default:
        this.emit("raw", msg);
        return;
    }
  }

  // ---------------- DEVICE_CONTEXT ----------------

  private handleDeviceContext(msg: DeviceContextMessage): void {
    if (typeof msg.PingTimeout === "number" && msg.PingTimeout > 0) {
      // Manager wants a heartbeat at PingTimeout/2; treat no traffic for
      // PingTimeout+50% as dead.
      this.pingIntervalMs = Math.max(1000, Math.floor(msg.PingTimeout / 2));
      this.deadAfterMs    = Math.floor(msg.PingTimeout * 1.5);
      // Restart timers with the new cadence.
      this.startPingTimers();
    }
    if (msg.SecondaryNetworkAddress) {
      this._secondaryAddress = msg.SecondaryNetworkAddress;
    }
    this.emit("device-context", msg);
  }

  // ---------------- handshake ----------------

  private handleServerConfig(msg: ServerConfigMessage): void {
    this._voipPort         = msg.VoipPort;
    this._audioSampleRate  = msg.AudioSampleRate;
    this._audioBitRate     = msg.AudioBitRate;
    this._audioFrameSize   = msg.AudioFrameSize;
    this._bufferSamplesCount = (this._audioSampleRate / 1000) * this._audioFrameSize;

    // Random 32-bit SSRC for our outgoing RTP frames
    const u32 = new Uint32Array(1);
    crypto.getRandomValues(u32);
    this._ssrc = u32[0];

    this.setState("configuring");
    if (this.pendingLogin) this.sendDeviceConfig();
  }

  private sendDeviceConfig(): void {
    if (!this.pendingLogin) return;
    this.send({
      MessageID: "DEVICE_CONFIG",
      Ssrc:        this._ssrc,
      AppName:     this.appName,
      VersionName: this.appVersion,
      VersionCode: 1,
      AudioCodec:  this._audioCodec,
      // Manager quirk: STRING here, BOOLEAN below — see docs/protocol.md 3.2
      VoiceOverTcp: "True",
      Password:    this.pendingLogin.password,
      DeviceData: {
        SessionID:         getBase64ID(),
        ID:                getDeviceID(),
        DeviceDescription: "MANUFACTURER=WLLC;MODEL=APIClientWEB;SERIAL=123456789;OSVERSION=5.0",
        Login:             this.pendingLogin.user,
        AvatarHash:        "",
        VoiceOverTcp:      true,
        StatusID:          NULL_GUID_B64,
      },
    });
    // Don't clear pendingLogin yet — we may need it on reconnect.
  }

  private handleLoginResponse(msg: LoginResponseMessage): void {
    if (msg.Response === LoginResponseCode.Success && msg.UserID) {
      this._userId = msg.UserID;
      if (this.loginResolve) {
        this.loginResolve({ userId: msg.UserID });
        this.loginResolve = this.loginReject = null;
      }
      // BEGIN_INITIALIZE will follow shortly — state stays "logging-in" until then.
      return;
    }
    const message = LOGIN_RESPONSE_MESSAGES[msg.Response]
      ?? `LOGIN_RESPONSE: unknown code ${msg.Response}`;
    const err = Object.assign(new Error(message), { code: msg.Response });
    if (this.loginReject) {
      this.loginReject(err);
      this.loginResolve = this.loginReject = null;
    }
    this.emit("auth-error", msg.Response, message);
    this.setState("error");
  }

  // ---------------- DATAEX (the roster) ----------------

  private handleDataEx(msg: DataExMessage): void {
    const { DataType, Operation, NetworkID, DataObjects } = msg;

    // DataType=40 (DEVICE_PARAMETERS) is special: the delta is on the top-level
    // message and DataObjects is null. Apply onto the device's Parameters.
    if (DataType === ObjectType.DeviceParameters) {
      this.applyDeviceParameters(msg as unknown as DeviceParametersPushMessage);
      return;
    }

    if (!Array.isArray(DataObjects)) return;
    // DataObjects on the wire is unknown[] until we've keyed by DataType;
    // narrow per-case below.
    const items = DataObjects as Array<{ ID: Base64Guid; [key: string]: unknown }>;

    switch (DataType) {
      case ObjectType.Networks:
        this.applyNetworks(Operation, items as unknown as Network[]);
        break;
      case ObjectType.Users:
        if (NetworkID) this.applyChildren(NetworkID, "users", "user", Operation, items);
        break;
      case ObjectType.Groups:
        if (NetworkID) this.applyChildren(NetworkID, "groups", "group", Operation, items);
        break;
      case ObjectType.Devices:
        if (NetworkID) this.applyChildren(NetworkID, "devices", "device", Operation, items);
        break;
      case ObjectType.Statuses:
        if (NetworkID) this.applyChildren(NetworkID, "statuses", undefined, Operation, items);
        break;
      case ObjectType.EmergencyProfiles:
        if (NetworkID) this.applyChildren(NetworkID, "emergency", undefined, Operation, items);
        break;
      case ObjectType.GroupPatches:
        // Out of scope for current rebuild — surface as raw so consumers
        // that care can opt in. (User direction: defer group patches.)
        this.emit("raw", msg);
        break;
      case ObjectType.ExtendedFieldsTemplate:
      case ObjectType.Media:
      case ObjectType.SipContacts:
      case ObjectType.Dispatchers:
      case ObjectType.LocationRequests:
      case ObjectType.AvatarResponse:
      case ObjectType.NetworkSettings:
      default:
        this.emit("raw", msg);
    }
  }

  /** Apply a DataType=40 push frame onto the device's Parameters cache. */
  private applyDeviceParameters(msg: DeviceParametersPushMessage): void {
    const net = this._networks.find(n => n.ID === msg.NetworkID);
    if (!net) return;
    const devices = (net.devices ?? []) as Device[];
    const dev = devices.find(d => d.ID === msg.DeviceID);
    if (!dev) return;

    const params: Device["Parameters"] = { ...(dev.Parameters ?? {}) };
    if (msg.BATTERY_CHARGING != null)  params.BATTERY_CHARGING  = msg.BATTERY_CHARGING;
    if (msg.BATTERY_LEVEL    != null)  params.BATTERY_LEVEL     = msg.BATTERY_LEVEL;
    if (msg.SelectedGroupID  != null)  params.SelectedGroupID   = msg.SelectedGroupID;
    if (msg.SelectedGroupName != null) params.SelectedGroupName = msg.SelectedGroupName;
    dev.Parameters = params;

    this.emit("device:change", dev, msg.NetworkID);
    this.emit("networks", this._networks);
  }

  private applyNetworks(op: DataOp, items: Network[]): void {
    if (op === DataOp.Initialize) {
      // Seed cache. Preserve child arrays already populated (init ordering
      // on the wire isn't guaranteed; defensive).
      this._networks = items.map(n => ({
        ...n,
        users:     n.users     ?? [],
        groups:    n.groups    ?? [],
        devices:   n.devices   ?? [],
        statuses:  n.statuses  ?? [],
        emergency: n.emergency ?? [],
        patches:   n.patches   ?? [],
      }));
      for (const n of this._networks) this.emit("network:add", n);
    } else if (op === DataOp.Add) {
      for (const n of items) {
        const fresh = { ...n, users: [], groups: [], devices: [], statuses: [], emergency: [], patches: [] };
        this._networks.push(fresh);
        this.emit("network:add", fresh);
      }
    } else if (op === DataOp.Remove) {
      for (const n of items) {
        const i = this._networks.findIndex(x => x.ID === n.ID);
        if (i >= 0) {
          const [removed] = this._networks.splice(i, 1);
          this.emit("network:remove", removed);
        }
      }
    } else if (op === DataOp.Change) {
      for (const n of items) {
        const i = this._networks.findIndex(x => x.ID === n.ID);
        if (i >= 0) {
          this._networks[i] = { ...this._networks[i], ...n };
          this.emit("network:change", this._networks[i]);
        }
      }
    }
    this.emit("networks", this._networks);
  }

  /**
   * Generic child-collection mutator (users/groups/devices/statuses/etc).
   * `eventBase` (if provided) drives the `xxx:add/change/remove` events.
   *
   * Items are accepted as a permissive bag with at least an `ID`. Type
   * narrowing happens at consumer-event boundaries via the typed
   * MCPTTClientEvents map; internally we treat the network's child
   * arrays as a generic record.
   */
  private applyChildren(
    networkId: Base64Guid,
    field: "users" | "groups" | "devices" | "statuses" | "emergency" | "patches",
    eventBase: "user" | "group" | "device" | undefined,
    op: DataOp,
    items: Array<{ ID: Base64Guid; [key: string]: unknown }>,
  ): void {
    const net = this._networks.find(n => n.ID === networkId);
    if (!net) {
      // Manager pushed children before the parent network; queue would
      // be ideal, but the bundle just drops these too. Emit raw for
      // observability.
      this.log("DATAEX for unknown network", networkId, field);
      return;
    }
    // Treat the network's field as a generic typed array via a local
    // alias — the strongly-typed view is exposed through `client.networks`
    // and the per-event payloads.
    const netRec = net as unknown as Record<string, Array<{ ID: Base64Guid }>>;
    if (!netRec[field]) netRec[field] = [];
    const arr = netRec[field];

    if (op === DataOp.Initialize) {
      netRec[field] = items.slice();
      if (eventBase) for (const it of items) this.emitEntity(eventBase, "add", it, networkId);
    } else if (op === DataOp.Add) {
      for (const it of items) {
        arr.push(it);
        if (eventBase) this.emitEntity(eventBase, "add", it, networkId);
      }
    } else if (op === DataOp.Remove) {
      for (const it of items) {
        const i = arr.findIndex(x => x.ID === it.ID);
        if (i >= 0) {
          const [removed] = arr.splice(i, 1);
          if (eventBase) this.emitEntity(eventBase, "remove", removed, networkId);
        }
      }
    } else if (op === DataOp.Change) {
      for (const it of items) {
        const i = arr.findIndex(x => x.ID === it.ID);
        if (i >= 0) {
          arr[i] = { ...arr[i], ...it };
          if (eventBase) this.emitEntity(eventBase, "change", arr[i], networkId);
        } else {
          // Change for an unknown child — treat as add.
          arr.push(it);
          if (eventBase) this.emitEntity(eventBase, "add", it, networkId);
        }
      }
    }
    this.emit("networks", this._networks);
  }

  private emitEntity(
    base: "user" | "group" | "device",
    kind: "add" | "change" | "remove",
    entity: { ID: Base64Guid },
    networkId: Base64Guid,
  ): void {
    // The event map narrows per-base/kind; cast through the loose
    // listener storage to a permissive callable since we lose the
    // discriminated narrowing inside the dispatch loop.
    const evt = `${base}:${kind}`;
    const set = this.listeners.get(evt);
    if (!set) return;
    for (const cb of Array.from(set)) {
      try { cb(entity, networkId); }
      catch (err) { console.error("MCPTTClient: listener for", evt, "threw:", err); }
    }
  }

  // ---------------- PTT ----------------

  private handlePttControl(msg: PttControlMessage): void {
    let call = this._calls.find(c => c.CallID === msg.CallID);

    const c = msg.Control;
    const isPrivate = c === PttControlType.VoicePrivateBegin    || c === PttControlType.VoicePrivateEnter
                   || c === PttControlType.VoicePrivatePressed  || c === PttControlType.VoicePrivateReleased
                   || c === PttControlType.VoicePrivateEnd      || c === PttControlType.VideoPrivateBegin
                   || c === PttControlType.VideoPrivateEnter    || c === PttControlType.VideoPrivatePressed
                   || c === PttControlType.VideoPrivateReleased || c === PttControlType.VideoPrivateEnd;
    const isVideo   = c === PttControlType.VideoPrivateBegin    || c === PttControlType.VideoPrivateEnter
                   || c === PttControlType.VideoPrivatePressed  || c === PttControlType.VideoPrivateReleased
                   || c === PttControlType.VideoPrivateEnd      || c === PttControlType.VideoGroupBegin
                   || c === PttControlType.VideoGroupEnter      || c === PttControlType.VideoGroupPressed
                   || c === PttControlType.VideoGroupReleased   || c === PttControlType.VideoGroupEnd;
    const kind: Call["kind"] = isVideo
      ? (isPrivate ? "video-private" : "video-group")
      : (isPrivate ? "voice-private" : "voice-group");

    const isBegin    = c === PttControlType.VoicePrivateBegin    || c === PttControlType.VoiceGroupBegin
                    || c === PttControlType.VoicePrivateEnter    || c === PttControlType.VoiceGroupEnter
                    || c === PttControlType.VideoPrivateBegin    || c === PttControlType.VideoGroupBegin
                    || c === PttControlType.VideoPrivateEnter    || c === PttControlType.VideoGroupEnter;
    const isPressed  = c === PttControlType.VoicePrivatePressed  || c === PttControlType.VoiceGroupPressed
                    || c === PttControlType.VideoPrivatePressed  || c === PttControlType.VideoGroupPressed;
    const isReleased = c === PttControlType.VoicePrivateReleased || c === PttControlType.VoiceGroupReleased
                    || c === PttControlType.VideoPrivateReleased || c === PttControlType.VideoGroupReleased;
    const isEnd      = c === PttControlType.VoicePrivateEnd      || c === PttControlType.VoiceGroupEnd
                    || c === PttControlType.VideoPrivateEnd      || c === PttControlType.VideoGroupEnd;

    if (!call && (isBegin || isPressed)) {
      call = {
        CallID:        msg.CallID,
        NetworkID:     msg.NetworkID,
        SourceID:      msg.SourceID,
        SourceUserID:  msg.SourceUserID,
        SourceName:    msg.SourceName,
        TargetID:      msg.TargetID,
        TargetName:    msg.TargetName,
        kind,
        state:         "initiated",
        startedAt:     Date.now(),
      };
      this._calls.push(call);
    }
    if (!call) return;  // RELEASED/END for a call we never saw — drop

    // Update display fields if a later frame in the same call carries
    // fresher Source/Target names.
    if (msg.SourceName) call.SourceName = msg.SourceName;
    if (msg.TargetName) call.TargetName = msg.TargetName;

    if (isPressed) {
      call.state = "speaking";
      this._stats.lastPressedTs = Date.now();
      // Snapshot RX count at press start so we can detect "this whole
      // press produced zero VOICE_PACKETs" on release. Strong signal
      // that the dispatcher account isn't authorized to monitor this
      // group on the manager.
      (call as Call & { _rxCountAtPress?: number })._rxCountAtPress = this._stats.voiceRxCount;
      this.emit("rx:start", call);
    } else if (isReleased) {
      call.state = "hangtime";
      this._stats.lastReleasedTs = Date.now();
      const before = (call as Call & { _rxCountAtPress?: number })._rxCountAtPress ?? this._stats.voiceRxCount;
      if (this._stats.voiceRxCount === before) {
        this._stats.silentCalls++;
      }
      this.emit("rx:stop", call);
    } else if (isEnd) {
      call.state = "ended";
      this.ackedCallIds.delete(msg.CallID);
      // Remove after a short grace so listeners can still inspect it
      setTimeout(() => {
        const i = this._calls.findIndex(x => x.CallID === msg.CallID);
        if (i >= 0) this._calls.splice(i, 1);
      }, 1000);
    }

    // Required PTT_RESPONSE ack — see ackedCallIds field comment for
    // why. Send once per CallID, when we first see Pressed (which
    // arrives in the same WSS frame as Begin/Enter from the manager).
    // Without this ack, the manager will silently stop fanning voice
    // for this group's calls.
    if (isPressed && !this.ackedCallIds.has(msg.CallID) && msg.SourceID
        && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ackedCallIds.add(msg.CallID);
      this.send({
        MessageID:   "PTT_RESPONSE",
        NetworkID:   msg.NetworkID,
        Destination: msg.SourceID,   // the device that initiated the call
        Response:    0,
        Type:        0,
      });
    }

    this.emit("ptt", call, msg.Control);
  }

  private handleVoicePacket(msg: VoicePacketMessage): void {
    const udp = base64ToUint8Array(msg.Data);
    // Always count the frame, even if we drop it for being too short
    // or muted — the diagnostics panel needs to know "frames are
    // arriving" vs "nothing is arriving".
    this._stats.voiceRxCount++;
    this._stats.voiceRxBytes  += udp.length;
    this._stats.voiceRxLastTs  = Date.now();
    if (udp.length < 28) return;
    // CallID lives in bytes 12..28 of the RTP-like header. See ServerApi.processVoicePacket.
    const callIdBytes = udp.subarray(12, 28);
    const callId = uint8ArrayToBase64(callIdBytes);
    const call = this._calls.find(c => c.CallID === callId);
    if (call?.TargetID && this.mutedTargetIds.has(call.TargetID)) return;
    if (call?.SoundIsMuted) return;
    if (call) this.emit("voice", call, udp);
    this.audio.playFrame(udp, callId);
  }

  private sendVoicePacket(frame: Uint8Array): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const Data = uint8ArrayToBase64(frame);
    this._stats.voiceTxCount++;
    this._stats.voiceTxBytes  += frame.length;
    this._stats.voiceTxLastTs  = Date.now();
    for (const NetworkID of this.pttNetworkIDs) {
      this.send({ MessageID: "VOICE_PACKET", NetworkID, Data });
    }
  }

  // ---------------- GPS ----------------

  private handleGpsResult(msg: {
    NetworkID: Base64Guid;
    DeviceID:  Base64Guid;
    Latitude:  number;
    Longitude: number;
    Accuracy:  number;
    Altitude?: number;
    Speed?:    number;
    Time:      number;
  }): void {
    const net = this._networks.find(n => n.ID === msg.NetworkID);
    if (!net) return;
    const devices = (net.devices ?? []) as Device[];
    const dev = devices.find(d => d.ID === msg.DeviceID);
    if (!dev) return;
    dev.Latitude  = msg.Latitude;
    dev.Longitude = msg.Longitude;
    dev.Accuracy  = msg.Accuracy;
    if (msg.Altitude != null) dev.Altitude = msg.Altitude;
    if (msg.Speed    != null) dev.Speed    = msg.Speed;
    dev.Time = msg.Time;
    this.emit("device:gps", dev, msg.NetworkID);
    this.emit("device:change", dev, msg.NetworkID);
  }

  // ---------------- Status changes ----------------

  private handleStatusChange(msg: { NetworkID: Base64Guid; DeviceID: Base64Guid; StatusID: Base64Guid }): void {
    const net = this._networks.find(n => n.ID === msg.NetworkID);
    if (!net) return;
    const devices = (net.devices ?? []) as Device[];
    const dev = devices.find(d => d.ID === msg.DeviceID);
    if (!dev) return;
    dev.StatusID = msg.StatusID === NULL_GUID_B64 ? undefined : msg.StatusID;
    this.emit("device:change", dev, msg.NetworkID);
  }

  // ============================================================
  // Ping / pong keepalive
  // ============================================================

  private startPingTimers(): void {
    this.clearPingTimers();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ MessageID: "PING" });
      }
    }, this.pingIntervalMs);
    this.deadCheckTimer = setInterval(() => {
      if (Date.now() - this.lastSeenAt > this.deadAfterMs) {
        this.log("dead connection — forcing close");
        try { this.ws?.close(4000, "dead"); } catch { /* ignore */ }
      }
    }, this.pingIntervalMs);
  }

  private clearPingTimers(): void {
    if (this.pingTimer)      { clearInterval(this.pingTimer);      this.pingTimer      = null; }
    if (this.deadCheckTimer) { clearInterval(this.deadCheckTimer); this.deadCheckTimer = null; }
  }

  // ============================================================
  // Helpers
  // ============================================================

  private send(message: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log("send while not open, dropping:", message.MessageID);
      return;
    }
    const wire = JSON.stringify([message]);
    if (this.debug) this.log("→", message.MessageID, message);
    try { this.ws.send(wire); } catch (err) { this.log("send error", err); }
  }

  private setState(s: ConnectionState): void {
    if (this._state === s) return;
    this._state = s;
    this.emit("state", s);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearCachedState(): void {
    this._networks = [];
    this._calls = [];
    this.emit("networks", this._networks);
  }

  private log(...args: unknown[]): void {
    if (!this.debug) return;
    // eslint-disable-next-line no-console
    console.log("[MCPTTClient]", ...args);
  }
}
